import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, Home } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';

const MONTHS_IN_YEAR = 12;

export const BuyVsRentCalculator = () => {
  const [propertyPrice, setPropertyPrice] = useState(8000000);
  const [downPaymentPct, setDownPaymentPct] = useState(20);
  const [loanRate, setLoanRate] = useState(9);
  const [tenureYears, setTenureYears] = useState(20);
  const [monthlyRent, setMonthlyRent] = useState(25000);
  const [rentIncrease, setRentIncrease] = useState(7);
  const [appreciation, setAppreciation] = useState(6);

  const [results, setResults] = useState({
    emi: 0, totalBuyCost: 0, totalRentCost: 0, futureValue: 0, netBuyPosition: 0, verdict: ''
  });

  const calculate = useCallback(() => {
    const downPayment = propertyPrice * (downPaymentPct / 100);
    const loanAmount = propertyPrice - downPayment;
    const months = tenureYears * MONTHS_IN_YEAR;
    const r = loanRate / MONTHS_IN_YEAR / 100;
    const emi = (loanAmount * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);

    const totalBuyCost = downPayment + emi * months;

    let totalRentCost = 0;
    let rent = monthlyRent;
    for (let y = 0; y < tenureYears; y += 1) {
      totalRentCost += rent * MONTHS_IN_YEAR;
      rent *= 1 + rentIncrease / 100;
    }

    const futureValue = propertyPrice * Math.pow(1 + appreciation / 100, tenureYears);
    const netBuyPosition = futureValue - totalBuyCost;
    const buyingBetter = (totalBuyCost - futureValue) < totalRentCost;

    setResults({
      emi, totalBuyCost, totalRentCost, futureValue, netBuyPosition,
      verdict: buyingBetter ? 'Buying looks better for you' : 'Renting looks better for you'
    });
  }, [propertyPrice, downPaymentPct, loanRate, tenureYears, monthlyRent, rentIncrease, appreciation]);

  useEffect(() => { calculate(); }, [calculate]);

  const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0
  }).format(value);

  return (
    <section className="py-20 bg-white" data-testid="buy-vs-rent-calculator">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          <Card className="border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-6 h-6 text-emerald-600" />
                Buy vs Rent Inputs
              </CardTitle>
              <CardDescription>Compare buying a home against renting over the long term</CardDescription>
            </CardHeader>
            <CardContent className="space-y-7">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Property Price</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(propertyPrice)}</span>
                </div>
                <Slider value={[propertyPrice]} onValueChange={(v) => setPropertyPrice(v[0])} min={2000000} max={100000000} step={500000} data-testid="bvr-price-slider" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Down Payment</Label>
                  <span className="text-xl font-bold text-emerald-600">{downPaymentPct}%</span>
                </div>
                <Slider value={[downPaymentPct]} onValueChange={(v) => setDownPaymentPct(v[0])} min={10} max={50} step={5} data-testid="bvr-downpayment-slider" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Home Loan Rate (p.a.)</Label>
                  <span className="text-xl font-bold text-emerald-600">{loanRate.toFixed(1)}%</span>
                </div>
                <Slider value={[loanRate]} onValueChange={(v) => setLoanRate(v[0])} min={7} max={13} step={0.1} data-testid="bvr-rate-slider" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Loan Tenure / Comparison Period</Label>
                  <span className="text-xl font-bold text-emerald-600">{tenureYears} years</span>
                </div>
                <Slider value={[tenureYears]} onValueChange={(v) => setTenureYears(v[0])} min={5} max={30} step={1} data-testid="bvr-tenure-slider" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Current Monthly Rent</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(monthlyRent)}</span>
                </div>
                <Slider value={[monthlyRent]} onValueChange={(v) => setMonthlyRent(v[0])} min={5000} max={300000} step={5000} data-testid="bvr-rent-slider" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Yearly Rent Increase</Label>
                  <span className="text-xl font-bold text-emerald-600">{rentIncrease}%</span>
                </div>
                <Slider value={[rentIncrease]} onValueChange={(v) => setRentIncrease(v[0])} min={0} max={15} step={1} data-testid="bvr-rent-increase-slider" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Property Appreciation (p.a.)</Label>
                  <span className="text-xl font-bold text-emerald-600">{appreciation}%</span>
                </div>
                <Slider value={[appreciation]} onValueChange={(v) => setAppreciation(v[0])} min={0} max={15} step={1} data-testid="bvr-appreciation-slider" />
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white">
              <CardHeader>
                <CardTitle className="text-emerald-700">Buy vs Rent Comparison</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-white p-6 rounded-xl border-2 border-emerald-200 text-center">
                  <p className="text-sm text-slate-600 mb-2">Verdict over {tenureYears} years</p>
                  <p className="text-2xl font-bold text-emerald-600" data-testid="bvr-verdict">{results.verdict}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Monthly EMI (if you buy)</p>
                    <p className="text-xl font-bold text-slate-900" data-testid="bvr-emi">{formatCurrency(results.emi)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Total Cost of Buying</p>
                    <p className="text-xl font-bold text-slate-900">{formatCurrency(results.totalBuyCost)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Total Rent Paid</p>
                    <p className="text-xl font-bold text-slate-900" data-testid="bvr-total-rent">{formatCurrency(results.totalRentCost)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Property Value After {tenureYears} yrs</p>
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(results.futureValue)}</p>
                  </div>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200 text-center">
                  <p className="text-sm text-slate-600">Net Wealth Gain from Buying</p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="bvr-net-position">{formatCurrency(results.netBuyPosition)}</p>
                  <p className="text-xs text-slate-500 mt-1">property value minus total amount paid</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
              <CardContent className="pt-6">
                <Home className="w-8 h-8 mb-2" />
                <p className="text-sm opacity-90 mb-1">Rule of thumb</p>
                <p className="text-sm opacity-90">
                  Rent money is an expense; EMI builds an asset. If you plan to stay 7+ years in the same
                  city, buying usually wins — especially when property appreciation beats rent inflation.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
